import {
  CASE_PLAN_ACCESS_PERMISSIONS,
  CASE_PLAN_EDIT_PLAN_PERMISSION,
  CASE_PLAN_REENTRY_CREATE_PERMISSION,
  REENTRY_PLAN_TYPES,
  REENTRY_VISIBILITY_PLAN_TYPES,
  mapEntitledPlanTypesToApi,
  toVisibilityPlanType,
} from '../constants/reentry-planning.constants';

const LAST_REFRESHED_TIME_OPTIONS = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

export const formatLastRefreshedText = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', LAST_REFRESHED_TIME_OPTIONS)}`;
};

const normalizePermissionName = (permissionName) => permissionName?.toLowerCase() ?? '';

const CASE_PLAN_ACCESS_PERMISSIONS_SET = new Set(
  CASE_PLAN_ACCESS_PERMISSIONS.map((permission) => permission.toLowerCase()),
);

export const hasCasePlanAccess = (permissions, permissionIds) =>
  permissions?.some(
    (permission) =>
      CASE_PLAN_ACCESS_PERMISSIONS_SET.has(normalizePermissionName(permission.permissionName)) &&
      permissionIds?.includes(permission.permissionId),
  );

export const hasCasePlanEditPermission = (permissions, permissionIds) =>
  permissions?.some(
    (permission) =>
      normalizePermissionName(permission.permissionName) ===
      CASE_PLAN_EDIT_PLAN_PERMISSION.toLowerCase() &&
      permissionIds?.includes(permission.permissionId),
  );

export const hasCasePlanCreatePermission = (permissions, permissionIds) =>
  permissions?.some(
    (permission) =>
      normalizePermissionName(permission.permissionName) ===
      CASE_PLAN_REENTRY_CREATE_PERMISSION.toLowerCase() &&
      permissionIds?.includes(permission.permissionId),
  );

const isPlanTypeFeatureEnabled = (locationFeatures, visibilityPlanType) =>
  locationFeatures?.some(
    (feature) =>
      feature?.status === true &&
      toVisibilityPlanType(feature?.featureName) === visibilityPlanType,
  ) === true;

export const resolveEntitledPlanTypesFromFeatures = (locationFeatures) =>
  REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) =>
    isPlanTypeFeatureEnabled(locationFeatures, planTypeKey),
  );

export const isReentryPlanFeatureEnabled = (locationFeatures) =>
  resolveEntitledPlanTypesFromFeatures(locationFeatures).includes(REENTRY_PLAN_TYPES.MVP.feature);

export const shouldShowCreatePlanButton = (entitledPlanTypes = []) =>
  (entitledPlanTypes.includes(REENTRY_PLAN_TYPES.PREVIEW.feature) && entitledPlanTypes.includes(REENTRY_PLAN_TYPES.MVP.feature)) || entitledPlanTypes.includes(REENTRY_PLAN_TYPES.MVP.feature);

export const getEffectiveActiveFeatures = (location, allLocations = []) => {
  if (Array.isArray(location?.activeFeatures) && location.activeFeatures.length > 0) {
    return location.activeFeatures;
  }

  if (location?.parentLocationId) {
    const parent = allLocations.find((item) => item.locationId === location.parentLocationId);
    return parent?.activeFeatures || [];
  }

  return [];
};

const resolveVisibilityPlanTypesFromActiveFeatures = (activeFeatures) => {
  const entitledPlanTypes = new Set();

  (activeFeatures || []).forEach((featureName) => {
    const visibilityKey = toVisibilityPlanType(featureName);
    if (visibilityKey) {
      entitledPlanTypes.add(visibilityKey);
    }
  });

  return REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) => entitledPlanTypes.has(planTypeKey));
};

const filterLocationsByScope = (locations, scopeLocationIds) => {
  if (!scopeLocationIds) {
    return locations;
  }

  return locations.filter((location) => scopeLocationIds.has(location.locationId));
};

export const resolveEntitledPlanTypesFromLocations = (locations, scopeLocationIds) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }

  const scopedLocations = filterLocationsByScope(locations, scopeLocationIds);
  const entitledPlanTypes = new Set();

  scopedLocations.forEach((location) => {
    const activeFeatures = getEffectiveActiveFeatures(location, locations);
    resolveVisibilityPlanTypesFromActiveFeatures(activeFeatures).forEach((planTypeKey) => {
      entitledPlanTypes.add(planTypeKey);
    });
  });

  return REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) => entitledPlanTypes.has(planTypeKey));
};

export const hasReentryPlanTypeInLocations = (locations) =>
  resolveEntitledPlanTypesFromLocations(locations ?? []).length > 0;

/**
 * Module-shell access gate: permission required; superadmin bypasses location LFAs;
 * otherwise any entitled plan type at locations grants access.
 *
 * Side-nav intentionally does not use this helper — it requires both permission and
 * LFA entitlement with no superadmin bypass (product decision).
 */
export const canAccessReentryPlanning = (
  permissions,
  permissionIds,
  { isSuperAdmin = false, locations = [] } = {},
) => {
  if (!hasCasePlanAccess(permissions, permissionIds)) {
    return false;
  }

  if (isSuperAdmin) {
    return true;
  }

  return hasReentryPlanTypeInLocations(locations);
};

export const resolvePlanTypesFromLocations = (locations, scopeLocationIds) =>
  mapEntitledPlanTypesToApi(resolveEntitledPlanTypesFromLocations(locations, scopeLocationIds));

export const createViewPlanClickHandler = (onViewPlan, plan) => () => {
  onViewPlan(plan);
};

export const REENTRY_PLANS_LOCATION_COLUMN = 'location';

const FACILITY_LOCATION_TYPE_ID = 3;
const HOUSING_UNIT_LOCATION_TYPE_ID = 15;

const compareByLocationName = (a, b) =>
  String(a.locationName).localeCompare(String(b.locationName));

const pickFacilitiesSorted = (locations) =>
  locations
    .filter((item) => item.locationTypeId === FACILITY_LOCATION_TYPE_ID)
    .sort(compareByLocationName);

const pickHousingUnitsForFacility = (locations, facility) =>
  locations
    .filter(
      (item) =>
        item.parentLocationId === facility.locationId &&
        item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID,
    )
    .sort(compareByLocationName);

const buildFacilityWithHousingUnits = (locations, facility) => [
  facility,
  ...pickHousingUnitsForFacility(locations, facility),
];

const toNonEmptyLocationName = (item) => {
  const name = item?.locationName;
  return name != null && String(name).trim() !== '' ? String(name) : null;
};

export const getLocationFilterOptions = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return pickFacilitiesSorted(locations)
    .flatMap((facility) => buildFacilityWithHousingUnits(locations, facility))
    .map(toNonEmptyLocationName)
    .filter(Boolean);
};

export const mapLocationFilterToLocationIds = (filters, locations = []) => {
  const filter = Array.isArray(filters)
    ? filters.find((item) => item.columnName === REENTRY_PLANS_LOCATION_COLUMN)
    : null;

  if (!filter?.value) {
    return undefined;
  }

  const locationNames = String(filter.value)
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  if (locationNames.length === 0) {
    return undefined;
  }

  const locationIds = locations
    .filter(
      (item) =>
        locationNames.includes(item.locationName) &&
        (item.locationTypeId === FACILITY_LOCATION_TYPE_ID ||
          item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID),
    )
    .map((item) => item.locationId);

  return locationIds.length > 0 ? locationIds.join(',') : undefined;
};
